import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import StatusBadge from "@/components/base/StatusBadge";
import EmptyState from "@/components/base/EmptyState";
import { useAuth } from "@/context/AuthContext";
import { getOwnerOrders } from "@/services/ownerApi";
import type { OwnerOrder } from "@/types";

const statusFilters = ["all", "paid", "pending", "refunded"] as const;

export default function OwnerOrders() {
  const { token } = useAuth();
  const [orders, setOrders] = useState<OwnerOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [status, setStatus] = useState<(typeof statusFilters)[number]>("all");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getOwnerOrders(token)
      .then((o) => active && setOrders(o))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const filtered = useMemo(
    () => (status === "all" ? orders : orders.filter((o) => o.status === status)),
    [orders, status],
  );

  const totals = useMemo(() => {
    const paid = orders.filter((o) => o.status === "paid").reduce((s, o) => s + o.amount, 0);
    const pending = orders.filter((o) => o.status === "pending").reduce((s, o) => s + o.amount, 0);
    return { paid, pending };
  }, [orders]);

  return (
    <>
      <Seo title="Orders & Invoices" path="/dashboard/orders" />

      <div>
        <h1 className="font-heading text-2xl font-extrabold text-foreground-950">Orders &amp; invoices</h1>
        <p className="mt-1 text-sm text-foreground-600">Every plan, add-on and feature purchase on your account.</p>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Total orders</span>
          <p className="mt-2 font-heading text-2xl font-extrabold text-foreground-950">{orders.length}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Amount paid</span>
          <p className="mt-2 font-heading text-2xl font-extrabold text-accent-700">₹{totals.paid.toLocaleString("en-IN")}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Due</span>
          <p className="mt-2 font-heading text-2xl font-extrabold text-secondary-700">₹{totals.pending.toLocaleString("en-IN")}</p>
        </div>
      </div>

      <div className="mt-6 inline-flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
        {statusFilters.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setStatus(f)}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold capitalize transition-colors whitespace-nowrap cursor-pointer ${
              status === f ? "bg-primary-500 text-foreground-950" : "text-foreground-600 hover:bg-background-100"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="h-64 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
        ) : error ? (
          <EmptyState icon="ri-wifi-off-line" title="Couldn't load your orders" description="Please refresh and try again." />
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-file-list-3-line" title="No orders here" description="Orders you place for plans and add-ons will appear here." />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-background-200 bg-background-50">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="bg-background-100/70 text-left text-xs uppercase tracking-wide text-foreground-500">
                  <th className="px-4 py-3 font-semibold">Order</th>
                  <th className="px-4 py-3 font-semibold">Item</th>
                  <th className="px-4 py-3 font-semibold">Invoice</th>
                  <th className="px-4 py-3 font-semibold">Payment</th>
                  <th className="px-4 py-3 font-semibold">Amount</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => (
                  <tr key={o.id} className="border-t border-background-100">
                    <td className="px-4 py-3 font-mono text-xs text-foreground-700">{o.orderNo}</td>
                    <td className="px-4 py-3 text-foreground-800">{o.planName}</td>
                    <td className="px-4 py-3 font-mono text-xs text-foreground-600">{o.invoiceNo}</td>
                    <td className="px-4 py-3 text-foreground-600">{o.paymentMode}</td>
                    <td className="px-4 py-3 font-semibold text-foreground-900">₹{o.amount.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                    <td className="px-4 py-3 text-foreground-500">{o.createdAt}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-xs font-semibold text-foreground-700 hover:border-primary-400 whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-download-2-line"></i> Invoice
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-6 rounded-lg border border-background-200 bg-background-100/60 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-base font-bold text-foreground-950">Need more visibility?</h2>
          <p className="mt-1 text-sm text-foreground-600">Upgrade your plan for featured placement and a verified badge.</p>
        </div>
        <Link
          to="/listing-plans"
          className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-vip-crown-line"></i> View plans
        </Link>
      </div>
    </>
  );
}